/**
 * Компонент управления внешними источниками данных (KlsExternalSources)
 * 
 * Функциональность:
 * - Отображение списка внешних источников
 * - Создание нового источника с многошаговой формой
 * - Редактирование существующего источника
 * - Удаление источника
 * - Тестирование подключения к источнику
 * - Запуск синхронизации
 * - Настройка маппинга таблиц и полей
 * - Настройка расписания синхронизации (cron)
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PermissionGuard } from "@/components/PermissionGuard";
import { klsExternalSourcesApi } from "@/services/kls-api.service";
import type {
  KlsExternalSources,
  KlsExternalSourcesCreate,
  KlsExternalSourcesUpdate,
} from "@/types/api-v2";
import {
  externalSourceSchema,
  type ExternalSourceFormData,
} from "@/lib/validations/kls";
import {
  Plus,
  Pencil,
  Trash2,
  TestTube,
  Play,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ReferencesSectionHeader } from "@/components/admin/ReferencesSectionHeader";

export default function ExternalSourcesManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<KlsExternalSources | null>(null);
  const [showConnectionString, setShowConnectionString] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    item: KlsExternalSources | null;
  }>({ isOpen: false, item: null });

  // Форма с валидацией
  const form = useForm<ExternalSourceFormData>({
    resolver: zodResolver(externalSourceSchema),
    defaultValues: {
      name: "",
      description: "",
      source_type: "postgresql",
      connection_string: "",
      schema_name: "",
      table_mapping: {},
      sync_schedule: "",
      auto_sync: false,
      sync_mode: "incremental",
      is_active: true,
    },
  });

  const sourceType = form.watch("source_type");

  // Загрузка списка источников
  // Добавляем staleTime для кэширования, чтобы не делать запрос каждый раз при открытии вкладки
  const { data: sources = [], isLoading } = useQuery({
    queryKey: ["kls-external-sources"],
    queryFn: () => klsExternalSourcesApi.list({ is_active: true }),
    staleTime: 30000, // Кэш на 30 секунд
    gcTime: 300000, // Хранить в кэше 5 минут
  });

  // Мутация создания
  const createMutation = useMutation({
    mutationFn: (data: KlsExternalSourcesCreate) => klsExternalSourcesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kls-external-sources"] });
      toast({
        title: "Успешно",
        description: "Внешний источник создан",
      });
      setIsDialogOpen(false);
      form.reset();
      setTestResult(null);
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось создать внешний источник",
        variant: "destructive",
      });
    },
  });

  // Мутация обновления
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: KlsExternalSourcesUpdate }) =>
      klsExternalSourcesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kls-external-sources"] });
      toast({
        title: "Успешно",
        description: "Внешний источник обновлен",
      });
      setIsDialogOpen(false);
      setEditingItem(null);
      form.reset();
      setTestResult(null);
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось обновить внешний источник",
        variant: "destructive",
      });
    },
  });

  // Мутация удаления
  const deleteMutation = useMutation({
    mutationFn: (id: number) => klsExternalSourcesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kls-external-sources"] });
      toast({
        title: "Успешно",
        description: "Внешний источник удален",
      });
      setDeleteConfirm({ isOpen: false, item: null });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось удалить внешний источник",
        variant: "destructive",
      });
    },
  });

  // Тестирование подключения
  const testConnectionMutation = useMutation({
    mutationFn: (sourceId: number) => klsExternalSourcesApi.testConnection(sourceId),
    onSuccess: (data) => {
      setTestResult({
        success: data.success,
        message: data.message,
      });
      if (data.success) {
        toast({
          title: "Успешно",
          description: "Подключение установлено",
        });
      } else {
        toast({
          title: "Ошибка",
          description: data.message,
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      setTestResult({
        success: false,
        message: error?.response?.data?.detail || "Ошибка при тестировании подключения",
      });
      toast({
        title: "Ошибка",
        description: "Не удалось протестировать подключение",
        variant: "destructive",
      });
    },
  });

  // Запуск синхронизации
  const syncMutation = useMutation({
    mutationFn: ({ sourceId, dryRun }: { sourceId: number; dryRun?: boolean }) =>
      klsExternalSourcesApi.sync(sourceId, { dry_run: dryRun }),
    onSuccess: (data) => {
      toast({
        title: "Успешно",
        description: `Синхронизация запущена. Обработано записей: ${data.records_processed || 0}`,
      });
      queryClient.invalidateQueries({ queryKey: ["kls-sync-logs"] });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось запустить синхронизацию",
        variant: "destructive",
      });
    },
  });

  // Открытие диалога создания/редактирования
  const openDialog = (item?: KlsExternalSources) => {
    if (item) {
      setEditingItem(item);
      form.reset({
        name: item.name,
        description: item.description || "",
        source_type: item.source_type as any,
        connection_string: item.connection_string,
        schema_name: item.schema_name || "",
        table_mapping: item.table_mapping || {},
        sync_schedule: item.sync_schedule || "",
        auto_sync: item.auto_sync,
        sync_mode: item.sync_mode as any,
        is_active: item.is_active,
      });
    } else {
      setEditingItem(null);
      form.reset({
        name: "",
        description: "",
        source_type: "postgresql",
        connection_string: "",
        schema_name: "",
        table_mapping: {},
        sync_schedule: "",
        auto_sync: false,
        sync_mode: "incremental",
        is_active: true,
      });
    }
    setIsDialogOpen(true);
    setTestResult(null);
    setShowConnectionString(false);
  };

  // Обработка отправки формы
  const onSubmit = (data: ExternalSourceFormData) => {
    if (editingItem) {
      updateMutation.mutate({
        id: editingItem.id,
        data: {
          name: data.name,
          description: data.description || undefined,
          source_type: data.source_type,
          connection_string: data.connection_string,
          schema_name: data.schema_name || undefined,
          table_mapping: data.table_mapping,
          sync_schedule: data.sync_schedule || undefined,
          auto_sync: data.auto_sync,
          sync_mode: data.sync_mode,
          is_active: data.is_active,
        },
      });
    } else {
      createMutation.mutate({
        name: data.name,
        description: data.description || undefined,
        source_type: data.source_type,
        connection_string: data.connection_string,
        schema_name: data.schema_name || undefined,
        table_mapping: data.table_mapping || {},
        sync_schedule: data.sync_schedule || undefined,
        auto_sync: data.auto_sync,
        sync_mode: data.sync_mode,
        is_active: data.is_active,
      });
    }
  };

  // Форматирование статуса синхронизации
  const getSyncStatusBadge = (source: KlsExternalSources) => {
    if (!source.last_sync_status) {
      return <Badge variant="outline">Не синхронизирован</Badge>;
    }
    switch (source.last_sync_status.toLowerCase()) {
      case "success":
        return <Badge variant="default" className="bg-green-500">Успешно</Badge>;
      case "error":
        return <Badge variant="destructive">Ошибка</Badge>;
      case "partial":
        return <Badge variant="secondary">Частично</Badge>;
      case "running":
        return <Badge variant="secondary">Выполняется</Badge>;
      default:
        return <Badge variant="outline">{source.last_sync_status}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <ReferencesSectionHeader
        title="Внешние источники данных"
        description="Подключения к внешним БД и параметры импорта разделов KLS: строка подключения, маппинг таблиц, расписание синхронизации."
        actions={
          <PermissionGuard permission="kls.sources.create">
            <Button onClick={() => openDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              Добавить источник
            </Button>
          </PermissionGuard>
        }
      />

      {/* Таблица */}
      <div className="border rounded-md overflow-hidden">
        <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Название</TableHead>
                <TableHead>Тип источника</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Автосинхронизация</TableHead>
                <TableHead>Последняя синхронизация</TableHead>
                <TableHead>Статус синхронизации</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-6">
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : sources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-6">
                    Внешние источники отсутствуют
                  </TableCell>
                </TableRow>
              ) : (
                sources.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.source_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {item.is_active ? (
                        <Badge variant="default">Активен</Badge>
                      ) : (
                        <Badge variant="secondary">Неактивен</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.auto_sync ? (
                        <Badge variant="default">
                          <Clock className="mr-1 h-3 w-3" />
                          Включена
                        </Badge>
                      ) : (
                        <Badge variant="outline">Выключена</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.last_sync_at
                        ? new Date(item.last_sync_at).toLocaleString("ru-RU")
                        : "-"}
                    </TableCell>
                    <TableCell>{getSyncStatusBadge(item)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <PermissionGuard permission="kls.sources.view">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setIsTestingConnection(true);
                              testConnectionMutation.mutate(item.id, {
                                onSettled: () => setIsTestingConnection(false),
                              });
                            }}
                            disabled={isTestingConnection}
                            title="Тест подключения"
                          >
                            <TestTube className="h-4 w-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="kls.sources.sync">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              syncMutation.mutate({ sourceId: item.id, dryRun: false });
                            }}
                            disabled={syncMutation.isPending}
                            title="Запустить синхронизацию"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="kls.sources.edit">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="kls.sources.delete">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm({ isOpen: true, item })}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Диалог создания/редактирования */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Редактировать внешний источник" : "Создать внешний источник"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic">Основная информация</TabsTrigger>
                  <TabsTrigger value="connection">Подключение</TabsTrigger>
                  <TabsTrigger value="mapping">Маппинг</TabsTrigger>
                  <TabsTrigger value="sync">Синхронизация</TabsTrigger>
                </TabsList>

                {/* Основная информация */}
                <TabsContent value="basic" className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Название *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Источник данных" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Описание</FormLabel>
                        <FormControl>
                          <Textarea {...field} placeholder="Описание источника" rows={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="source_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Тип источника *</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Выберите тип источника" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="postgresql">PostgreSQL</SelectItem>
                            <SelectItem value="mysql">MySQL</SelectItem>
                            <SelectItem value="mssql">MS SQL Server</SelectItem>
                            <SelectItem value="oracle">Oracle</SelectItem>
                            <SelectItem value="file">Файл (CSV/JSON/XML)</SelectItem>
                            <SelectItem value="api">REST API</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Активен</FormLabel>
                          <FormDescription>
                            Включить/выключить источник данных
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </TabsContent>

                {/* Подключение */}
                <TabsContent value="connection" className="space-y-4">
                  <FormField
                    control={form.control}
                    name="connection_string"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Строка подключения *</FormLabel>
                        <div className="relative">
                          <FormControl>
                            <Input
                              {...field}
                              type={showConnectionString ? "text" : "password"}
                              placeholder={
                                sourceType === "postgresql"
                                  ? "postgresql://user:password@host:port/database"
                                  : sourceType === "file"
                                  ? "/path/to/file.csv"
                                  : sourceType === "api"
                                  ? "https://api.example.com/data"
                                  : "Строка подключения"
                              }
                            />
                          </FormControl>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 transform -translate-y-1/2"
                            onClick={() => setShowConnectionString(!showConnectionString)}
                          >
                            {showConnectionString ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <FormDescription>
                          {sourceType === "postgresql" &&
                            "Формат: postgresql://user:password@host:port/database"}
                          {sourceType === "file" && "Путь к файлу на сервере"}
                          {sourceType === "api" && "URL API эндпоинта"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {(sourceType === "postgresql" ||
                    sourceType === "mysql" ||
                    sourceType === "mssql" ||
                    sourceType === "oracle") && (
                    <FormField
                      control={form.control}
                      name="schema_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Имя схемы</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="public" />
                          </FormControl>
                          <FormDescription>Имя схемы в базе данных</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  {editingItem && (
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsTestingConnection(true);
                          testConnectionMutation.mutate(editingItem.id, {
                            onSettled: () => setIsTestingConnection(false),
                          });
                        }}
                        disabled={isTestingConnection}
                      >
                        <TestTube className="mr-2 h-4 w-4" />
                        Тест подключения
                      </Button>
                      {testResult && (
                        <Alert
                          variant={testResult.success ? "default" : "destructive"}
                        >
                          <div className="flex items-center gap-2">
                            {testResult.success ? (
                              <CheckCircle2 className="h-4 w-4" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                            <AlertDescription>{testResult.message}</AlertDescription>
                          </div>
                        </Alert>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Маппинг */}
                <TabsContent value="mapping" className="space-y-4">
                  <FormField
                    control={form.control}
                    name="table_mapping"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Маппинг таблиц и полей (JSON)</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={JSON.stringify(field.value || {}, null, 2)}
                            onChange={(e) => {
                              try {
                                const parsed = JSON.parse(e.target.value);
                                field.onChange(parsed);
                              } catch {
                                // Игнорируем ошибки парсинга во время ввода
                              }
                            }}
                            placeholder='{"table_name": "kls", "fields": {"kls_namef": "name", "kls_code": "code"}}'
                            rows={10}
                            className="font-mono text-sm"
                          />
                        </FormControl>
                        <FormDescription>
                          JSON объект с маппингом таблиц и полей из внешнего источника
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                {/* Синхронизация */}
                <TabsContent value="sync" className="space-y-4">
                  <FormField
                    control={form.control}
                    name="sync_mode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Режим синхронизации</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="full">Полная синхронизация</SelectItem>
                            <SelectItem value="incremental">Инкрементальная</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Полная - заменяет все данные, инкрементальная - обновляет только
                          измененные
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="auto_sync"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Автоматическая синхронизация</FormLabel>
                          <FormDescription>
                            Включить автоматическую синхронизацию по расписанию
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                  {form.watch("auto_sync") && (
                    <FormField
                      control={form.control}
                      name="sync_schedule"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Расписание (cron)</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="0 0 * * *"
                              className="font-mono"
                            />
                          </FormControl>
                          <FormDescription>
                            Cron выражение (например: 0 0 * * * - каждый день в полночь)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    form.reset();
                    setEditingItem(null);
                    setTestResult(null);
                  }}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingItem ? "Сохранить" : "Создать"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, item: null })}
        onConfirm={() => {
          if (deleteConfirm.item) {
            deleteMutation.mutate(deleteConfirm.item.id);
          }
        }}
        title="Удалить внешний источник?"
        description={`Вы уверены, что хотите удалить источник "${deleteConfirm.item?.name}"?`}
        confirmText="Удалить"
        cancelText="Отмена"
        variant="destructive"
      />
    </div>
  );
}
